import { useState } from 'react';
import { FormIcon } from '../FormIcon.jsx';
import { AUDIENCES, LAYOUT_TYPES } from '../formSchema.js';
import { Check, ClearableSelect, CopyField, Field, ListInput, Toggle } from './controls.jsx';
import { SearchableSelect } from '../../components/SearchableSelect.jsx';

const SECTIONS = [
  { id: 'general', label: 'General', icon: 'general' },
  { id: 'notifications', label: 'Notifications', icon: 'bell' },
  { id: 'embed', label: 'Website & Embed', icon: 'globe' },
  { id: 'portal', label: 'Client Portal', icon: 'portal' },
  { id: 'integrations', label: 'Integrations & CRM', icon: 'integrations' },
  { id: 'access', label: 'Access & Permissions', icon: 'users' },
  { id: 'advanced', label: 'Advanced', icon: 'advanced' },
];

const BASE_VARS = ['form_name', 'submitter_name', 'submitter_email', 'client_name', 'project_name', 'submitted_at', 'answers', 'link', 'organization_name'];

function SectionHead({ title, subtitle }) {
  return (
    <header className="fm-section-head">
      <h3>{title}</h3>
      {subtitle ? <p>{subtitle}</p> : null}
    </header>
  );
}

function Variables({ fields }) {
  const keys = fields.filter((f) => !LAYOUT_TYPES.includes(f.type)).map((f) => f.key);
  return (
    <div className="fm-vars">
      <span>Template variables:</span>
      {[...BASE_VARS, ...keys].map((v) => (
        <code key={v}>{`{{${v}}}`}</code>
      ))}
    </div>
  );
}

function embedSnippets(form, settings) {
  const url = `${form.public_url}?embed=1`;
  const id = `a360-form-${form.public_key}`;
  const listener = `<script>window.addEventListener("message",function(e){var d=e.data||{};if(d.a360Form==="${form.public_key}"&&d.height){var f=document.getElementById("${id}");if(f)f.style.height=d.height+"px";}});</script>`;
  const inline = `<iframe id="${id}" src="${url}" title="${form.title.replace(/"/g, '&quot;')}" style="width:100%;max-width:760px;border:0;min-height:480px" loading="lazy"></iframe>\n${listener}`;
  const label = (settings.embed.button_label || 'Open form').replace(/</g, '&lt;');
  const popup = `<button type="button" onclick="a360OpenForm_${form.public_key}()" style="background:#1463ff;color:#fff;border:0;border-radius:10px;padding:12px 20px;font:600 15px system-ui;cursor:pointer">${label}</button>
<script>function a360OpenForm_${form.public_key}(){var o=document.createElement("div");o.style.cssText="position:fixed;inset:0;background:rgba(15,23,42,.55);z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px";o.onclick=function(e){if(e.target===o)o.remove();};var f=document.createElement("iframe");f.id="${id}";f.src="${url}";f.style.cssText="width:100%;max-width:720px;height:80vh;border:0;border-radius:14px;background:#fff";o.appendChild(f);document.body.appendChild(o);}</script>
${listener}`;
  return { inline, popup };
}

export function FormSettingsTab({ form, draft, patch, patchSettings, options, onCopied }) {
  const [section, setSection] = useState('general');
  const s = draft.settings;
  const inputFields = draft.fields.filter((f) => !LAYOUT_TYPES.includes(f.type));
  const clientFacing = draft.audience === 'client' || draft.audience === 'project';
  const projects = (options.projects || []).filter((p) => !draft.client_id || p.client_id === draft.client_id);
  const client = (options.clients || []).find((c) => c.id === draft.client_id);
  const snippets = embedSnippets(form, s);

  const fieldSelect = (value, onChange, filter) => (
    <div className="fm-select-wrap">
      <SearchableSelect value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">Auto-detect</option>
        {inputFields.filter(filter).map((f) => (
          <option key={f.id} value={f.key}>
            {f.label}
          </option>
        ))}
      </SearchableSelect>
      <FormIcon name="chevronDown" size={16} />
    </div>
  );

  return (
    <div className="fm-settings">
      <nav className="fm-card fm-subnav" aria-label="Settings sections">
        {SECTIONS.map((item) => (
          <button key={item.id} type="button" className={`fm-subnav-item${section === item.id ? ' is-on' : ''}`} onClick={() => setSection(item.id)}>
            <FormIcon name={item.icon} size={17} />
            {item.label}
          </button>
        ))}
      </nav>

      <section className="fm-card fm-settings-main">
        {section === 'general' ? (
          <>
            <SectionHead title="General Settings" subtitle="Basic information about this form." />
            <Field label="Form name">
              <input value={draft.title} onChange={(e) => patch({ title: e.target.value })} />
            </Field>
            <Field label="Description">
              <textarea rows={3} value={draft.description || ''} onChange={(e) => patch({ description: e.target.value })} placeholder="Shown under the title in the banner." />
            </Field>
            <div className="fm-grid-2">
              <Field label="Submit button text">
                <input value={s.general.submit_label} onChange={(e) => patchSettings('general', { submit_label: e.target.value })} />
              </Field>
              <Field label="Success message">
                <textarea rows={3} value={s.general.success_message} onChange={(e) => patchSettings('general', { success_message: e.target.value })} />
              </Field>
            </div>
            <Field label="Redirect after submit" optional hint="Send respondents to this URL instead of showing the success message.">
              <input type="url" value={s.general.redirect_url} placeholder="https://yoursite.com/thank-you" onChange={(e) => patchSettings('general', { redirect_url: e.target.value })} />
            </Field>
            <Toggle checked={s.general.banner} onChange={(v) => patchSettings('general', { banner: v })} label="Show header banner" hint="Dark banner with the title and description at the top of the form." />

            {clientFacing ? (
              <div className="fm-subsection">
                <h4>Client assignment</h4>
                <div className="fm-grid-2">
                  <Field label="Client">
                    <ClearableSelect
                      value={draft.client_id}
                      placeholder="Select a client"
                      options={options.clients || []}
                      onChange={(clientId) => patch({ client_id: clientId, project_id: null })}
                    />
                  </Field>
                  <Field label="Project" optional>
                    <ClearableSelect
                      value={draft.project_id}
                      placeholder={projects.length ? 'Select a project' : 'No projects'}
                      options={projects}
                      disabled={!projects.length}
                      onChange={(projectId) => {
                        const project = (options.projects || []).find((p) => p.id === projectId);
                        patch({ project_id: projectId, client_id: project ? project.client_id : draft.client_id });
                      }}
                    />
                  </Field>
                </div>
                <Field label="Due date" optional hint="Submissions after this date are flagged as late.">
                  <input type="date" value={draft.due_date || ''} onChange={(e) => patch({ due_date: e.target.value || null })} />
                </Field>
              </div>
            ) : null}
          </>
        ) : null}

        {section === 'notifications' ? (
          <>
            <SectionHead title="Notifications" subtitle="Who hears about new submissions, and what the respondent receives." />
            <Toggle
              checked={s.notifications.notify_team}
              onChange={(v) => patchSettings('notifications', { notify_team: v })}
              label="Notify team in the app"
              hint="Bell notification for staff who can see this form."
            />
            <div className="fm-subsection">
              <h4>Team email</h4>
              <Field label="Send to" hint="Comma-separated. Leave empty to skip email.">
                <ListInput value={s.notifications.team_emails} onChange={(v) => patchSettings('notifications', { team_emails: v })} placeholder="team@agency.com, pm@agency.com" />
              </Field>
              <Field label="Subject">
                <input value={s.notifications.team_subject} onChange={(e) => patchSettings('notifications', { team_subject: e.target.value })} />
              </Field>
              <Field label="Body">
                <textarea rows={5} value={s.notifications.team_body} onChange={(e) => patchSettings('notifications', { team_body: e.target.value })} />
              </Field>
            </div>
            <div className="fm-subsection">
              <Toggle
                checked={s.notifications.client_confirmation}
                onChange={(v) => patchSettings('notifications', { client_confirmation: v })}
                label="Send confirmation to respondent"
                hint="Emailed to the submitter (portal user email or the form's Email field)."
              />
              {s.notifications.client_confirmation ? (
                <>
                  <Field label="Subject">
                    <input value={s.notifications.confirmation_subject} onChange={(e) => patchSettings('notifications', { confirmation_subject: e.target.value })} />
                  </Field>
                  <Field label="Body">
                    <textarea rows={5} value={s.notifications.confirmation_body} onChange={(e) => patchSettings('notifications', { confirmation_body: e.target.value })} />
                  </Field>
                </>
              ) : null}
            </div>
            <Variables fields={draft.fields} />
          </>
        ) : null}

        {section === 'embed' ? (
          <>
            <SectionHead title="Website & Embed" subtitle="Share a link or place the form on any website." />
            {form.status === 'draft' ? <p className="fm-alert fm-alert--warn">Publish the form to make the link and embed live. Drafts are not reachable publicly.</p> : null}
            {draft.audience === 'internal' || draft.category === 'template' ? (
              <p className="fm-alert fm-alert--warn">Internal forms and templates have no public page. Switch the form type to “Website form” or “Send to a client”.</p>
            ) : null}
            <Field label="Form key">
              <CopyField value={form.public_key} onCopied={onCopied} />
            </Field>
            <Field label="Public link">
              <CopyField value={form.public_url} onCopied={onCopied} />
            </Field>
            <div className="fm-grid-2">
              <Field label="Used on (label)" hint="Shown in the forms list, e.g. “Marketing”.">
                <input value={s.embed.site_label} onChange={(e) => patchSettings('embed', { site_label: e.target.value })} placeholder="Marketing" />
              </Field>
              <Field label="Placement">
                <div className="fm-seg">
                  {[
                    ['inline', 'Inline'],
                    ['popup', 'Popup button'],
                  ].map(([id, label]) => (
                    <button key={id} type="button" className={s.embed.display === id ? 'is-on' : ''} onClick={() => patchSettings('embed', { display: id })}>
                      {label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            {s.embed.display === 'popup' ? (
              <Field label="Button label">
                <input value={s.embed.button_label} onChange={(e) => patchSettings('embed', { button_label: e.target.value })} />
              </Field>
            ) : null}
            <Field label="Embed code" hint="Paste into your site's HTML. The frame resizes to fit the form.">
              <CopyField multiline value={s.embed.display === 'popup' ? snippets.popup : snippets.inline} onCopied={onCopied} label="Copy code" />
            </Field>
            <Field label="Allowed websites" optional hint="Only accept embedded submissions from these domains (subdomains included). Empty = any site.">
              <ListInput value={s.embed.allowed_domains} onChange={(v) => patchSettings('embed', { allowed_domains: v })} placeholder="example.com, shop.example.com" />
            </Field>
          </>
        ) : null}

        {section === 'portal' ? (
          <>
            <SectionHead title="Client Portal" subtitle="How this form shows up for the assigned client." />
            {!clientFacing ? <p className="fm-alert fm-alert--warn">These options apply when the form type is “Send to a client” or “Add to a project”.</p> : null}
            {clientFacing && client ? (
              <p className="fm-hint">
                Assigned to <strong>{client.name}</strong>. Every active portal user for this client will see it.
              </p>
            ) : null}
            <Toggle
              checked={s.portal.show_in_portal}
              onChange={(v) => patchSettings('portal', { show_in_portal: v })}
              label={'Add to “We Need From You”'}
              hint="Lists the form on the client's portal under Forms."
              disabled={!clientFacing}
            />
            <Toggle
              checked={s.portal.notify_client}
              onChange={(v) => patchSettings('portal', { notify_client: v })}
              label="Send client notification on publish"
              hint="In-app notification to the client's portal users the first time it is published."
              disabled={!clientFacing}
            />
            <Toggle
              checked={s.portal.remind_if_incomplete}
              onChange={(v) => patchSettings('portal', { remind_if_incomplete: v })}
              label="Remind if incomplete"
              hint={draft.due_date ? 'Reminders go out before the due date and continue while it is overdue (at most every 2 days).' : 'Set a due date to enable reminders.'}
              disabled={!clientFacing || !draft.due_date}
            />
            {s.portal.remind_if_incomplete && draft.due_date ? (
              <Field label="Start reminding (days before due date)">
                <input type="number" min={1} max={30} value={s.portal.reminder_days} onChange={(e) => patchSettings('portal', { reminder_days: Number(e.target.value) || 1 })} />
              </Field>
            ) : null}
          </>
        ) : null}

        {section === 'integrations' ? (
          <>
            <SectionHead title="Integrations & CRM" subtitle="Push submissions into your CRM and project workflow." />
            <div className="fm-subsection">
              <h4>GoHighLevel</h4>
              {!options.ghl_connected ? <p className="fm-alert fm-alert--warn">GoHighLevel is not connected for this workspace. Connect it under Integrations to enable contact sync.</p> : null}
              <Toggle
                checked={s.integrations.ghl_upsert_contact}
                onChange={(v) => patchSettings('integrations', { ghl_upsert_contact: v })}
                label="Create / update contact"
                hint="Upserts a contact by email in the client's GHL sub-account (or your agency location for website forms)."
                disabled={!options.ghl_connected}
              />
              <Field label="Apply tags" hint="Comma-separated, added to the contact on every submission.">
                <ListInput value={s.integrations.ghl_tags} onChange={(v) => patchSettings('integrations', { ghl_tags: v })} placeholder="website-lead, discovery" />
              </Field>
              <div className="fm-grid-3">
                <Field label="Name field">{fieldSelect(s.integrations.map_name, (v) => patchSettings('integrations', { map_name: v }), (f) => ['text'].includes(f.type))}</Field>
                <Field label="Email field">{fieldSelect(s.integrations.map_email, (v) => patchSettings('integrations', { map_email: v }), (f) => ['email', 'text'].includes(f.type))}</Field>
                <Field label="Phone field">{fieldSelect(s.integrations.map_phone, (v) => patchSettings('integrations', { map_phone: v }), (f) => ['phone', 'text'].includes(f.type))}</Field>
              </div>
            </div>
            <div className="fm-subsection">
              <h4>Project task</h4>
              <Toggle
                checked={s.integrations.create_task}
                onChange={(v) => patchSettings('integrations', { create_task: v })}
                label="Create a task on submission"
                hint="Added to the assigned project (or the client's latest active project)."
              />
              {s.integrations.create_task ? (
                <div className="fm-grid-2">
                  <Field label="Task title">
                    <input value={s.integrations.task_title} onChange={(e) => patchSettings('integrations', { task_title: e.target.value })} />
                  </Field>
                  <Field label="Assign to">
                    <ClearableSelect
                      value={s.integrations.task_assignee_id}
                      placeholder="Unassigned"
                      options={options.staff || []}
                      onChange={(v) => patchSettings('integrations', { task_assignee_id: v })}
                    />
                  </Field>
                </div>
              ) : null}
            </div>
            <p className="fm-hint">
              Need more? Add rules in the <strong>Automations</strong> tab — webhooks, emails, extra tasks or tags when a submission arrives or is reviewed.
            </p>
          </>
        ) : null}

        {section === 'access' ? (
          <>
            <SectionHead title="Access & Permissions" subtitle="Who can see this form and how people can respond." />
            <Field label="Visible to staff">
              <div className="fm-seg">
                {[
                  ['all', 'All staff with client access'],
                  ['admins', 'Admins only'],
                ].map(([id, label]) => (
                  <button key={id} type="button" className={s.access.staff_visibility === id ? 'is-on' : ''} onClick={() => patchSettings('access', { staff_visibility: id })}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Toggle
              checked={s.access.allow_multiple}
              onChange={(v) => patchSettings('access', { allow_multiple: v })}
              label="Allow multiple submissions per client"
              hint="Off: client forms count as complete after the first response (1 / 1)."
            />
            <Toggle checked={s.access.collect_ip} onChange={(v) => patchSettings('access', { collect_ip: v })} label="Record submitter IP address" />
          </>
        ) : null}

        {section === 'advanced' ? (
          <>
            <SectionHead title="Advanced" subtitle="Limits, file handling and spam protection." />
            <div className="fm-grid-2">
              <Field label="Stop accepting responses after" optional>
                <input type="date" value={s.advanced.close_at || ''} onChange={(e) => patchSettings('advanced', { close_at: e.target.value || null })} />
              </Field>
              <Field label="Maximum submissions" hint="0 = unlimited">
                <input type="number" min={0} value={s.advanced.max_submissions} onChange={(e) => patchSettings('advanced', { max_submissions: Math.max(0, Number(e.target.value) || 0) })} />
              </Field>
            </div>
            <div className="fm-grid-2">
              <Field label="Default max file size (MB)">
                <input type="number" min={1} max={50} value={s.advanced.max_file_mb} onChange={(e) => patchSettings('advanced', { max_file_mb: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })} />
              </Field>
              <Field label="Default allowed file types">
                <ListInput value={s.advanced.allowed_file_types} onChange={(v) => patchSettings('advanced', { allowed_file_types: v.map((x) => x.replace(/^\./, '').toLowerCase()) })} />
              </Field>
            </div>
            <Toggle checked={s.advanced.honeypot} onChange={(v) => patchSettings('advanced', { honeypot: v })} label="Spam protection (honeypot)" hint="Silently drops bot submissions on public pages." />
          </>
        ) : null}
      </section>

      <aside className="fm-card fm-form-type">
        <h3>Form Type</h3>
        <p>How will this form be used?</p>
        <div className="fm-radio-cards" role="radiogroup" aria-label="Form type">
          {AUDIENCES.map((a) => (
            <label key={a.id} className={`fm-radio-card${draft.audience === a.id ? ' is-on' : ''}`}>
              <input
                type="radio"
                name="fm-audience"
                checked={draft.audience === a.id}
                onChange={() =>
                  patch((cur) => ({
                    audience: a.id,
                    category: a.id === 'website' ? (cur.category === 'template' ? 'template' : 'website_form') : cur.category === 'website_form' ? 'questionnaire' : cur.category,
                  }))
                }
              />
              <span>
                <strong>{a.label}</strong>
                <small>{a.hint}</small>
              </span>
            </label>
          ))}
        </div>
        {(draft.audience === 'client' || draft.audience === 'project') && !draft.client_id ? <p className="fm-hint is-warn">Choose a client in General before publishing.</p> : null}
        <Check
          checked={draft.category === 'template'}
          onChange={(v) => patch((cur) => ({ category: v ? 'template' : cur.audience === 'website' ? 'website_form' : 'questionnaire' }))}
          label="Keep as a reusable template"
          hint="Templates are never published; create forms from them."
        />
      </aside>
    </div>
  );
}
